import React, { useState, useEffect, useRef, useCallback} from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Chip
} from '@mui/material';
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import MarkdownRenderer from '../common/MarkdownRenderer';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import useNotification from '../../hooks/useNotification';
import { Accordion, EditableTextField } from '../atoms';
import LoadMoreButton from '../atoms/LoadMore';
import EmptyStateBox from '../atoms/EmptyStateBox';
import useSectionCRUD from '../../hooks/useSectionCRUD';
import useContentActions from '../../hooks/useContentActions';
import useExplain from '../../hooks/useExplain';
import useDesignReferenceVersions from '../../hooks/useDesignReferenceVersions';
import VersionSelector from '../atoms/VersionSelector';
import ExplanationModal from '../atoms/ExplanationModal';
import ImprovementModal from '../atoms/ImprovementModal';
import useFullScreen from '../../hooks/useFullScreen';
import TopBarButtons from '../atoms/TopBarButtons';
import ConfirmationDialog from '../common/ConfirmationDialog';
import SectionHeader from '../atoms/Header';
import LoadingBox from '../atoms/LoadingBox';
import { capitalizeFirst } from '../../utils/textTransformations';
import conversationService from '../../services/conversationService';
import useImprove from '../../hooks/useImprove';
import useParsedSections from '../../hooks/useParsedSections';
import InlineImproveInput from '../common/InlineImproveInput';
import ColorPalette from '../design-reference/ColorPalette';
import FileTypeIcon from '../design-reference/FileTypeIcon';

const SplitViewDesignReferences = ({ conversation, children, isStreaming, streamingContent, projectId, onConversationUpdate }) => {
  const { showNotification } = useNotification();
  const referencePanelRef = useRef(null);

  // Use version management for design references
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    updateCurrentVersion
  } = useDesignReferenceVersions(conversation, isStreaming, streamingContent);

  // useParsedSections hook — single source of truth for section data
  const {
    sections: parsedReferences,
    setSections: setParsedReferences,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef: referenceContentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  // State for managing current version's references
  const [expandedReferences, setExpandedReferences] = useState({});
  const [visibleReferences, setVisibleReferences] = useState(10);
  const isLocalUpdateRef = useRef(false);

  // Track previous version index to detect actual version changes
  const prevVersionIndexRef = useRef(currentVersionIndex);

  // Version switching: reset visible references when switching versions
  useEffect(() => {
    if (currentVersion) {
      const versionChanged = prevVersionIndexRef.current !== currentVersionIndex;
      if (versionChanged) {
        setVisibleReferences(10);
        prevVersionIndexRef.current = currentVersionIndex;
      }
    }
  }, [currentVersion, currentVersionIndex]);

  // Check if this is a design reference conversation
  const isDesignReferenceConversation =
    conversation?.taskId === 'design-references' ||
    conversation?.task_id === 'design-references' ||
    (conversation?.messages?.some(msg =>
      msg.role === 'assistant' &&
      (msg.content?.includes('# Design References Analysis') ||
       msg.content?.includes('## Color Schemes Summary') ||
       msg.content?.includes('**Color Palette:**'))
    ));

  const showReferencePanel = parsedReferences.length > 0;

  const handleReferenceToggle = (referenceId) => {
    setExpandedReferences(prev => ({
      ...prev,
      [referenceId]: !prev[referenceId]
    }));
  };

  const handleLoadMore = () => {
    setVisibleReferences(prev => Math.min(prev + 10, parsedReferences.length));
  };

  const handleShowAll = () => {
    setVisibleReferences(parsedReferences.length);
  };

  // Use CRUD operations hook
  const {
    editingSection: editingReference,
    editedContent,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setDeleteConfirmation,
    handleStartEdit: handleStartEditOriginal,
    handleCancelEdit,
    handleSaveEdit: handleSaveEditOriginal,
    handleDeleteSection: handleDeleteReferenceClick,
    handleDeleteSectionConfirmed: handleDeleteConfirmGeneric
  } = useSectionCRUD(
    conversation,
    parsedReferences,
    setParsedReferences,
    setExpandedReferences,
    referenceContentRef,
    updateCurrentVersion,
    currentVersion,
    isLocalUpdateRef,
    null, // onConversationUpdate
    markLocalEdit
  );

  // Custom delete handler
  const handleDeleteConfirm = async () => {
    const reference = deleteConfirmation.section;
    setDeleteConfirmation({ open: false, section: null });

    if (!reference) return;

    try {
      // Remove the reference from the array
      const updatedReferences = parsedReferences.filter(r => r.id !== reference.id);

      // Set the flag to prevent reset from currentVersion
      isLocalUpdateRef.current = true;
      setParsedReferences(updatedReferences);

      // Remove expanded state for this reference
      setExpandedReferences(prev => {
        const updated = { ...prev };
        delete updated[reference.id];
        return updated;
      });

      // Reconstruct the full content
      let updatedContent = '';
      let lastCategory = null;

      updatedReferences.forEach((ref, idx) => {
        // Add category header if this is the first reference in a new category
        if (ref.isFirstInCategory && ref.category && ref.category !== lastCategory) {
          if (idx > 0) updatedContent += '\n';
          updatedContent += `# ${ref.category}\n\n`;
          lastCategory = ref.category;
        }

        // Add reference header
        updatedContent += `## ${ref.title}\n\n`;

        // Add reference content
        if (ref.content) {
          updatedContent += `${ref.content}\n\n`;
        }
      });

      // Update content ref
      referenceContentRef.current = updatedContent.trim();

      // Update the current version with new content
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          references: updatedReferences
        });
      }

      // Save to backend
      const updatedMessages = [...conversation.messages];
      const currentVersionMessageId = currentVersion?.id;
      let messageUpdated = false;

      if (currentVersionMessageId) {
        for (let i = 0; i < updatedMessages.length; i++) {
          if (updatedMessages[i]._id === currentVersionMessageId ||
              updatedMessages[i].id === currentVersionMessageId) {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            messageUpdated = true;
            break;
          }
        }
      }

      if (!messageUpdated) {
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      showNotification({
        message: `"${reference.title}" deleted successfully`,
        type: 'success'
      });

    } catch (error) {
      console.error('Error deleting reference:', error);
      showNotification({
        message: `Failed to delete "${reference.title}"`,
        type: 'error'
      });
    }
  };

  // Use explain hook
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainSection,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Use improve hook
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    registerSectionRef,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Initialize validation state for sections with version-specific IDs
  const initialValidationState = parsedReferences.reduce((acc, section) => {
    const versionSpecificId = `${section.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    acc[versionSpecificId] = false;
    return acc;
  }, {});

  // Use the validation hook with version-specific IDs
  const {
    validatedItems: validatedSections,
    handleValidateItem: handleValidateSectionBase,
    getValidatorType,
    isAssignedValidator,
    validatorInfo,
  } = useValidation({
    conversationId: conversation?._id || conversation?.id,
    projectId,
    taskId: conversation?.taskId || conversation?.task_id,
    initialState: initialValidationState
  });

  // Section content refs for text selection
  const sectionContentRefs = React.useRef({});
  const getSectionContentRef = (sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  };

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: conversation?.taskId || conversation?.task_id || '',
  });

  // Wrapper function to handle version-specific validation
  const handleValidateSection = async (sectionId) => {
    const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    return handleValidateSectionBase(versionSpecificId);
  };

  // Use content actions hook
  const {
    isExporting,
    isSaving: isSavingDocument,
    handleExport: handleExportReferences,
    getExportContent,
    handleCopy: handleCopyReferences,
    handleRefresh: handleRefreshReferences
  } = useContentActions(referenceContentRef, 'Design References', projectId, conversation?._id);

  // Add full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // Create wrapper functions
  const handleStartEdit = (referenceId, reference) => {
    handleStartEditOriginal(referenceId, reference.content);
  };

  const handleSaveEdit = (referenceId) => {
    return handleSaveEditOriginal(referenceId);
  };

  const handleCopyReference = (reference) => {
    const textToCopy = `## ${reference.title}\n\n${reference.content}`;
    navigator.clipboard.writeText(textToCopy);
    showNotification({
      message: 'Reference copied to clipboard',
      type: 'success'
    });
  };

  const handleExplainReference = (reference) => {
    requestExplanation(reference);
  };

  const handleImproveReference = (reference) => {
    handleImproveClick(reference);
  };

  // Handle update from improvement modal
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;

    try {
      // Handle remove action — delete section from artifact
      if (improveSection.action === 'remove') {
        
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        // Trigger a conversation refresh to pick up the change
        if (typeof onConversationUpdate === 'function') onConversationUpdate();
        return;
      }


      const contentToUse = improveSection.mergedContent || improvedContent;

      const updatedReferences = [...parsedReferences].map(r => {
        if (r.id === improveSection.id) {
          return {
            ...r,
            content: contentToUse,
            rawContent: contentToUse
          };
        }
        return r;
      });

      isLocalUpdateRef.current = true;
      setParsedReferences(updatedReferences);

      // Reconstruct the full content
      let updatedContent = '';
      let lastCategory = null;

      updatedReferences.forEach((ref, idx) => {
        if (ref.isFirstInCategory && ref.category && ref.category !== lastCategory) {
          if (idx > 0) updatedContent += '\n';
          updatedContent += `# ${ref.category}\n\n`;
          lastCategory = ref.category;
        }

        updatedContent += `## ${ref.title}\n\n`;

        if (ref.content) {
          updatedContent += `${ref.content}\n\n`;
        }
      });

      referenceContentRef.current = updatedContent.trim();

      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          references: updatedReferences,
          content: updatedContent.trim(),
          parsedContent: updatedReferences
        });
      }

      const updatedMessages = [...conversation.messages];
      for (let i = updatedMessages.length - 1; i >= 0; i--) {
        if (updatedMessages[i].role === 'assistant') {
          updatedMessages[i] = {
            ...updatedMessages[i],
            content: updatedContent.trim()
          };
          break;
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      closeImprovementModal();

      showNotification({
        message: 'Reference improved successfully',
        type: 'success'
      });

    } catch (error) {
      showNotification({
        message: 'Failed to update improved content',
        type: 'error'
      });
    }
  };

  // Auto-scroll reference panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && referencePanelRef.current && isDesignReferenceConversation) {
      setTimeout(() => {
        if (referencePanelRef.current) {
          referencePanelRef.current.scrollTop = referencePanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [streamingContent, isStreaming, isDesignReferenceConversation]);

  // Don't show the split view if this isn't a design reference conversation
  if (!isDesignReferenceConversation || (!showReferencePanel && !isStreaming)) {
    return <>{children}</>;
  }

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      mt: 5,
    }}>
      {/* Main content with conditional split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(false),
          p: 2,
        }}>
          {children}
        </Box>

        {/* Reference accordion panel */}
        <Box
          ref={referencePanelRef}
          sx={{
            ...getRightPanelStyles(false),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              pb: 2,
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <SectionHeader label='Design References' />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {hasVersions && versions.length > 1 && (
                  <VersionSelector
                    versions={versions}
                    currentVersionIndex={currentVersionIndex}
                    onVersionChange={setCurrentVersionIndex}
                    disabled={isStreaming}
                  />
                )}
                <TopBarButtons
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  getExportContent={getExportContent}
                  onExport={handleExportReferences}
                  isExporting={isExporting}
                  onCopy={handleCopyReferences}
                  onRefresh={handleRefreshReferences}
                  disabled={!referenceContentRef.current}
                  exportTooltip="Export design references as markdown"
                  copyTooltip="Copy design references to clipboard"
                  refreshTooltip="Refresh design references"
                />
              </Box>
            </Box>


            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={conversation?.taskId || conversation?.task_id || ''}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedSections}
              totalItems={parsedReferences?.length || 0}
            />
            {/* Show parsed references or a placeholder */}
            {isStreaming ? (
              <LoadingBox message='Analyzing design references...' spinnerSize={40} />
            ) : showReferencePanel && parsedReferences.length > 0 ? (
              <Box sx={{
                p: 3,
                pt: 2,
                flex: 1,
                overflow: "auto",
                '&::-webkit-scrollbar': {
                  width: '8px',
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-track': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  marginTop: '5px',
                  marginBottom: '5px',
                },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.4)',
                  },
                },
              }}>
                {/* Display overview if present */}
                {currentVersion?.overview && (
                  <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Overview
                    </Typography>
                    <MarkdownRenderer content={currentVersion.overview} />
                  </Box>
                )}

                {/* Display all colors */}
                {currentVersion?.allColors && currentVersion.allColors.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <ColorPalette
                      colors={currentVersion.allColors}
                      title="All Colors from References"
                      size={45}
                    />
                  </Box>
                )}

                {/* Render references as accordions */}
                {parsedReferences.slice(0, visibleReferences).map((reference, index) => {
                  const referenceId = reference.id;
                  const versionSpecificId = `${referenceId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
                  const isValidated = validatedSections[versionSpecificId] || false;

                  return (
                    <React.Fragment key={referenceId}>
                      {/* Display category header */}
                      {reference.isFirstInCategory && reference.category && (
                        <Box sx={{
                          mt: index === 0 ? 0 : 3,
                          mb: 2,
                          px: 2
                        }}>
                          <SectionHeader label={reference.category} />
                        </Box>
                      )}
                      <Accordion
                        ref={(el) => registerSectionRef(referenceId, el)}
                        title={capitalizeFirst(reference.title)}
                        defaultExpanded={false}
                        expanded={expandedReferences[referenceId]}
                        onChange={(_, expanded) => handleReferenceToggle(referenceId)}
                        rightIcon={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {reference.fileType && (
                              <Chip
                                icon={<FileTypeIcon fileType={reference.fileType} />}
                                label={reference.fileType}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '11px', height: 24 }}
                              />
                            )}
                            <Box
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await handleValidateSection(referenceId);
                                } catch (error) {
                                  showNotification({
                                    message: `Failed to validate`,
                                    type: "error"
                                  });
                                }
                              }}
                              sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}
                            >
                              {isValidated ? (
                                <ValidatedIcon size={25} memberType={getValidatorType(versionSpecificId)} />
                              ) : (
                                <UnvalidatedIcon size={25} />
                              )}
                            </Box>
                          </Box>
                        }
                      >
                        {/* Reference content */}
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                          width: '93%',
                          textAlign: 'justify',
                          minHeight: "160px",
                          cursor: editingReference === referenceId ? 'default' : 'pointer',
                          maxHeight: 'calc(100vh - 350px)',
                          overflow: 'auto',
                          '&::-webkit-scrollbar': {
                            width: '8px',
                            backgroundColor: 'transparent',
                          },
                          '&::-webkit-scrollbar-track': {
                            borderRadius: '10px',
                            backgroundColor: 'rgba(0, 0, 0, 0.05)',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            borderRadius: '10px',
                            backgroundColor: 'rgba(0, 0, 0, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.4)',
                            },
                          },
                        }}
                        >
                          {editingReference === referenceId ? (
                            // Edit mode
                            <EditableTextField
                              value={editedContent}
                              onChange={(e) => setEditedContent(e.target.value)}
                            />
                          ) : (
                            // Normal content display
                            <Box>
                              {/* Show colors if present */}
                              {reference.colors && reference.colors.length > 0 && (
                                <ColorPalette
                                  colors={reference.colors}
                                  size={40}
                                />
                              )}

                              <Box ref={getSectionContentRef(referenceId)}>
                                <MarkdownRenderer content={reference.content || ''} />
                              </Box>
                              <TextSelectionPopup
                                containerRef={getSectionContentRef(referenceId)}
                                sectionId={referenceId}
                                onSubmitComment={addComment}
                                disabled={editingReference === referenceId}
                              />

                              {/* Inline improve input */}
                              {showImproveInput === referenceId && (
                                <InlineImproveInput
                                  improvePrompt={improvePrompt}
                                  onPromptChange={setImprovePrompt}
                                  onSend={() => handleSendImprove(reference)}
                                  onCancel={handleCancelImprove}
                                  promptText="How would you like to improve this reference?"
                                  placeholder="Describe the improvements you'd like..."
                                />
                              )}
                            </Box>
                          )}
                        </Box>

                        {/* Action buttons */}
                        <Box sx={{
                          position: 'absolute',
                          right: 10,
                          top: 10,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5
                        }}>
                          <ActionButtons
                            item={reference}
                            itemIndex={index}
                            featureId={referenceId}
                            editingFeature={editingReference}
                            isSaving={isSaving}
                            onEdit={() => handleStartEdit(referenceId, reference)}
                            onSave={() => handleSaveEdit(referenceId)}
                            onCancel={handleCancelEdit}
                            onImprove={() => handleImproveReference(reference)}
                            onExplain={() => handleExplainReference(reference)}
                            onCopy={() => handleCopyReference(reference)}
                            onDelete={() => handleDeleteReferenceClick(reference)}
                            vertical={true}
                          onComment={() => openCommentPanel(referenceId)}
                          commentCount={commentCounts[referenceId]?.open || 0}
                          isApproved={isValidated}
                          approvedByType={isValidated ? getValidatorType(versionSpecificId) : null}
                          />
                        </Box>
                      </Accordion>
                    </React.Fragment>
                  );
                })}

                {/* Load more button */}
                {parsedReferences.length > visibleReferences && (
                  <LoadMoreButton
                    remaining={parsedReferences.length - visibleReferences}
                    onLoadMore={handleLoadMore}
                    onShowAll={handleShowAll}
                    label="Load more"
                  />
                )}

              </Box>
            ) : (
              <EmptyStateBox message="No design references found in this conversation yet." />
            )}
          </Paper>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, section: null })}
        title="Delete design reference"
        message={`Are you sure you want to delete "${deleteConfirmation.section?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height="auto"
        onConfirm={handleDeleteConfirm}
      />

      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainSection?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        onUpdate={handleUpdateImprovedContent}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />

    

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (parsedReferences || []).find(s => s.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />
</Box>
  );
};

export default SplitViewDesignReferences;
